import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import jwt_decode from 'jwt-decode';
import { interval, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { CommonService } from 'src/app/services/common.service';
import { TokenUserInfo } from '../common-structures/token-user-info';

@Component({
	selector: 'app-update-token',
	templateUrl: './app-update-token.component.html',
	styleUrls: ['./app-update-token.component.css'],
})
export class AppUpdateTokenComponent implements OnInit, OnDestroy {
	tokenSubscription: Subscription;
	refreshIntervalSubscription: Subscription;
	offeset: number = 60;

	constructor(private _matDialog: MatDialog, private router: Router, public cmnServ: CommonService) {}

	/**
	 * @description refresh the token based on its expiration time (5 minutes before it expires)
	 */
	ngOnInit() {
		// Set up token refresh interval
		this.tokenSubscription = this.cmnServ.getOauthToken().subscribe((_token) => {
			if (_token && _token.expires_in && _token.access_token) {
				// Only check token validity if we have a complete token
				this.checkTokenValidityOnInit();
				
				// Set up refresh timer based on token expiration
				this.setupRefreshTimer(_token);
			}
		});
	}

	/**
	 * @description setup refresh timer based on token expiration time
	 */
	private setupRefreshTimer(token: any) {
		// Clean up any existing interval first
		if (this.refreshIntervalSubscription) {
			this.refreshIntervalSubscription.unsubscribe();
		}
		
		// Calculate refresh time: 5 minutes before token expires
		const expiresInMs = token.expires_in * 1000; // Convert seconds to milliseconds
		const refreshBufferMs = 5 * 60 * 1000; // 5 minutes buffer
		const refreshTimeMs = Math.max(expiresInMs - refreshBufferMs, 60000); // Minimum 1 minute
		
		// Set up single refresh timer based on token expiration
		this.refreshIntervalSubscription = interval(refreshTimeMs)
			.subscribe(() => this.refreshToken());
	}

	private checkTokenValidityOnInit() {
		this.cmnServ
			.getOauthToken()
			.pipe(take(1))
			.subscribe((token: any) => {
				if (token && token.access_token) {
					try {
						const decodedToken: TokenUserInfo = jwt_decode(token.access_token);
						const currentTime = new Date().getTime();
						const tokenExpiryTime = new Date(decodedToken.exp * 1000).getTime();
						
						// If token is expired or will expire in next 5 minutes, logout
						if (tokenExpiryTime <= (currentTime + 300000)) {
							this.onLogout();
						}
					} catch (error) {
						// Don't auto-logout on decode errors during initialization
					}
				}
			});
	}

	refreshToken() {
		this.cmnServ
			.getOauthToken()
			.pipe(take(1))
			.subscribe((originalToken: any) => {
				if (!originalToken || !originalToken.access_token) {
					this.onLogout();
					return;
				}

				try {
					const decodedToken: TokenUserInfo = jwt_decode(originalToken.access_token);
					const currentTime = new Date().getTime();
					const tokenExpiryTime = new Date(decodedToken.exp * 1000).getTime();
					
					// If token is still valid, try to refresh it
					if (tokenExpiryTime > currentTime) {
						if (originalToken.refresh_token) {
							this.cmnServ.getRefreshToken(originalToken.refresh_token).subscribe(
								(refresh_token) => {
									this.cmnServ.setOauthToken(refresh_token);
									
									// Restart timer with new token's expiration time
									this.setupRefreshTimer(refresh_token);
								},
								(error) => {
									this.onLogout();
								}
							);
						} else {
							console.log('No refresh token available, logging out');
							this.onLogout();
						}
					} else {
						console.log('Token expired during refresh attempt, logging out');
						this.onLogout();
					}
				} catch (error) {
					console.error('Error decoding token during refresh:', error);
					this.onLogout();
				}
			});
	}

	onLogout() {
		this._matDialog.closeAll();
		
		// Use CommonService to clear all authentication data
		this.cmnServ.clearAuthenticationData();
		
		this.router.navigateByUrl('/');
	}

	ngOnDestroy() {
		if (this.tokenSubscription) {
			this.tokenSubscription.unsubscribe();
		}
		if (this.refreshIntervalSubscription) {
			this.refreshIntervalSubscription.unsubscribe();
		}
	}
}
